import createClient from 'openapi-fetch';
import { env } from '@/app/env';
import { authFetch } from './auth';
import type { components, paths } from './schema.gen';

export type Schemas = components['schemas'];

/**
 * The one error body (docs/API.md, apps/api/app/core/exceptions.py): `{code, i18n_key, params, message,
 * request_id}`. FastAPI does not put it in openapi.json (handlers, not response models), so it is typed here.
 */
export type ErrorBody = {
  code: string;
  i18n_key: string;
  params: Record<string, unknown>;
  message: string;
  request_id: string | null;
};

/** A non-2xx response, carrying the API error body. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: ErrorBody;

  constructor(status: number, body: ErrorBody) {
    super(body.message || body.code);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }

  get code(): string {
    return this.body.code;
  }
}

export function isErrorBody(value: unknown): value is ErrorBody {
  return typeof value === 'object' && value !== null && 'code' in value && 'i18n_key' in value;
}

/** Turns any response into a typed ApiError, so queries can rely on `throw`. */
export function toApiError(status: number, error: unknown): ApiError {
  if (isErrorBody(error)) return new ApiError(status, error);
  return new ApiError(status, {
    code: status === 0 ? 'network_error' : 'unexpected_response',
    i18n_key: status === 0 ? 'errors.network_error' : 'errors.unexpected_response',
    params: {},
    message: typeof error === 'string' ? error : `HTTP ${status}`,
    request_id: '',
  });
}

/**
 * The typed client. Types come from apps/api/openapi.json (src/api/schema.gen.ts). `authFetch` adds the
 * bearer token and refreshes it
 * silently on a 401 `token_expired` (P1-E05).
 */
export const api = createClient<paths>({
  baseUrl: env.apiBaseUrl,
  credentials: 'include',
  fetch: authFetch,
});

/** Unwraps an openapi-fetch result: returns data or throws ApiError. */
export async function unwrap<T>(promise: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  const { data, error, response } = await promise;
  if (error !== undefined || !response.ok) throw toApiError(response.status, error);
  return data as T;
}
